import { useRef, useState } from 'react';
import { Edit2 } from '@/components/icons';
import { getBlockRealId } from '@/components/preview/block-selection/blockClipboard';
import { getInstrumentLabel } from '@/components/preview/block-selection/blockGroups';
import { stripSplitBlockId } from '@/components/preview/pagination/utils';
import { renameRichTextLabel } from '@/lib/rich-text';
import { useReportWizard } from '../context/ReportWizardContext';
import type { PanelScope } from '../context/types';
import type { TableBlock } from '@/types/report-payload';

/**
 * Grows the rename field to fit the name, so editing shows exactly what
 * reading showed. A single-line input would put a long instrument name behind
 * a horizontal scroll the moment the user went to correct it.
 */
const fitToContent = (field: HTMLTextAreaElement) => {
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight}px`;
};

interface InstrumentNameHeadingProps {
    scope: PanelScope;
}

/**
 * The tools panel's title while it is scoped to one instrument.
 *
 * The name wraps instead of truncating: half of "Escala Wechsler de
 * Inteligência para Crianças — Quarta Edição" names nothing, and this is the
 * only place the panel says which instrument it is talking about.
 *
 * It is also where the instrument gets renamed. The name is the anchor table's
 * caption — the same field the psychologist edits on the page — so the panel
 * writes to it directly instead of sending the user back to the canvas to find
 * the table they are already scoped to.
 */
export function InstrumentNameHeading({ scope }: InstrumentNameHeadingProps) {
    const { formData, handleUpdate, setPanelScope, isReadOnly } = useReportWizard();
    const [isEditing, setIsEditing] = useState(false);
    // Escape has to beat the field's own blur, and removing a focused node does
    // not reliably fire one, so the cancel is a flag the blur handler reads
    // rather than a race between two events.
    const cancelledRef = useRef(false);

    const blocks = formData.tests?.blocks ?? [];
    const anchorId = scope.anchorRealId ? stripSplitBlockId(scope.anchorRealId) : null;
    // Resolved from the live block list, never from the scope: the caption can
    // be edited on the page while the panel sits open on it.
    const anchor = anchorId
        ? blocks.find((block) => stripSplitBlockId(getBlockRealId(block)) === anchorId)
        : undefined;
    const instrument = anchor?.block_type === 'table' ? (anchor as TableBlock) : null;

    // A run with no table of its own borrows its name from the divider above
    // it, and "Blocos avulsos" names no block at all — neither is this
    // heading's to rename. `getInstrumentLabel` reads the caption's first
    // block, the same one `renameRichTextLabel` writes back to.
    const name = instrument ? getInstrumentLabel(instrument) : scope.label;
    const canRename = !isReadOnly && !!instrument;

    const handleSave = (value: string) => {
        setIsEditing(false);

        const nextName = value.trim();
        if (!instrument || !nextName || nextName === name) return;

        handleUpdate((prev) => {
            const currentTests = prev.tests ?? { section_title: '', blocks: [] };
            const currentBlocks = currentTests.blocks ?? [];

            const nextBlocks = currentBlocks.map((block) => {
                if (stripSplitBlockId(getBlockRealId(block)) !== anchorId) return block;

                return {
                    ...block,
                    description: renameRichTextLabel((block as TableBlock).description, nextName),
                    // Same rule the canvas editor follows: once a person edits
                    // what the IA wrote, the block stops counting as automatic.
                    automatic: false,
                };
            });

            return { tests: { ...currentTests, blocks: nextBlocks } };
        });

        // The scope carries the label everywhere else in the panel, so it
        // cannot keep the old name after the rename that just happened here.
        setPanelScope({ ...scope, label: nextName });
    };

    if (isEditing) {
        return (
            <textarea
                // The heading only becomes a field because a rename was just
                // asked for, so the caret belongs here.
                autoFocus
                rows={1}
                defaultValue={name}
                aria-label="Nome do instrumento"
                placeholder="Nome do instrumento"
                maxLength={200}
                className="min-w-0 flex-1 resize-none overflow-hidden rounded-md bg-surface px-2 py-1 text-lg font-semibold leading-tight text-foreground outline-none ring-1 ring-info"
                ref={(field) => {
                    if (field) fitToContent(field);
                }}
                onInput={(event) => fitToContent(event.currentTarget)}
                onFocus={(event) => event.currentTarget.select()}
                onBlur={(event) => {
                    if (cancelledRef.current) {
                        cancelledRef.current = false;
                        return;
                    }

                    handleSave(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        // Blur saves, so both ways out run one path.
                        event.currentTarget.blur();
                        return;
                    }

                    if (event.key === 'Escape') {
                        // The panel's own Escape drops the scope; this one only
                        // has to close the field.
                        event.preventDefault();
                        event.stopPropagation();
                        cancelledRef.current = true;
                        setIsEditing(false);
                    }
                }}
            />
        );
    }

    return (
        <>
            {/* One title, never a trail: the way back is the explicit "Voltar"
                control at the top of the body, so the header only has to say
                where you are. */}
            <h2 className="min-w-0 break-words text-lg font-semibold leading-tight text-foreground">
                {name}
            </h2>
            {canRename && (
                <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    aria-label="Renomear instrumento"
                    title="Renomear instrumento"
                    className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface hover:text-foreground focus:outline-none focus-visible:text-foreground"
                >
                    <Edit2 className="size-3.5" />
                </button>
            )}
        </>
    );
}
